import { fetchApi } from "@/lib/api";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

export type RiskFlagStatus = "OPEN" | "CLOSED";
export type RiskFlagPriority = "HIGH" | "MEDIUM" | "STANDARD";

export interface RiskFlagCategory {
  source: string;
  category: string;
  priority: string;
  confidence: number;
  explanation: string;
  subcategory: string;
}

export interface RiskFlagUser {
  id: string;
  name: string | null;
  first_name: string | null;
  last_name: string | null;
  role: string;
  profile_picture_url: string | null;
  redacted_fields?: string[];
}

export interface RiskFlagMentorship {
  id: string;
  sdk_url: string;
  mentor: RiskFlagUser;
  mentee: RiskFlagUser;
}

export interface RiskFlag {
  id: string;
  created_at: string;
  updated_at: string;
  sourceType: string;
  sourceId: string;
  sourceMessageId: string | null;
  user: RiskFlagUser;
  userId: string;
  mentorship: RiskFlagMentorship;
  mentorshipId: string;
  status: RiskFlagStatus;
  severity: string;
  priority: RiskFlagPriority;
  categories: RiskFlagCategory[];
  transcribedText: string | null;
  sourceContent: string | null;
  feedbackCorrect: boolean | null;
  feedbackNote: string | null;
  closedBy: string | null;
  closedByUser: { id: string; name: string } | null;
  resolutionReason: "resolved" | "expired" | null;
  metadata: Record<string, unknown>;
}

export interface MentorshipAlertGroup {
  mentorshipId: string;
  groupKey: string;
  priority: RiskFlagPriority;
  mentorship: RiskFlagMentorship;
  alertCount: number;
  latestAlert: RiskFlag;
  alerts: RiskFlag[];
}

interface GroupedRiskFlagResponse {
  groups: MentorshipAlertGroup[];
  totalGroups: number;
  page: number;
  limit: number;
  hasMore: boolean;
}
export const useRiskFlags = (
  status: RiskFlagStatus,
  page: number,
  limit: number,
  search?: string,
  priority?: RiskFlagPriority,
  category?: string,
  dateFrom?: string,
  dateTo?: string,
) => {
  return useQuery<GroupedRiskFlagResponse>({
    queryKey: ["risk-flags", status, page, limit, search, priority, category, dateFrom, dateTo],
    queryFn: async () => {
      const response = await fetchApi<GroupedRiskFlagResponse>({
        url: "/v2/content-moderation/alerts",
        method: "GET",
        params: {
          status,
          page,
          limit,
          groupBy: "mentorship",
          ...(search?.trim() ? { search: search.trim() } : {}),
          ...(priority ? { priority } : {}),
          ...(category ? { category } : {}),
          ...(dateFrom ? { dateFrom } : {}),
          ...(dateTo ? { dateTo } : {}),
        },
      });

      return response.data;
    },
  });
};

export const useResolveAlert = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      alertId,
      feedbackCorrect,
      feedbackNote,
    }: {
      alertId: string;
      feedbackCorrect: boolean;
      feedbackNote?: string;
    }) => {
      const response = await fetchApi({
        url: `/v2/content-moderation/alerts/${alertId}/resolve`,
        method: "POST",
        data: { feedbackCorrect, feedbackNote },
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["risk-flags"] });
      queryClient.invalidateQueries({ queryKey: ["risk-flag-count"] });
    },
  });
};

export const useRiskFlagCount = (enabled = true) => {
  const { data } = useQuery({
    queryKey: ["risk-flag-count"],
    queryFn: () =>
      fetchApi<{ count: number }>({ url: "/v2/content-moderation/alerts/count" }),
    enabled,
    refetchInterval: 30_000,
    refetchOnWindowFocus: true,
    refetchOnMount: true,
    staleTime: 10_000,
  });
  return data?.data?.count ?? 0;
};
